import MapIcon from '@mui/icons-material/Map';
import { Button, Stack, type Theme, Typography, useMediaQuery } from '@mui/material';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import EnvironmentSliderV2, { type EnvironmentSliderMarker } from '/@/components/environment-slider-v2';
import HumidityMapModal from '/@/components/humidity-map-modal';
import Spinner from '/@/components/spinner';
import { useLastCampaign } from '/@/hooks/crop-monitoring/use-last-campaign';
import { useHumidityMaps } from '/@/hooks/maps';
import { getClimatePatternChoices } from '/@/hooks/recommendation';
import { useToggle } from '/@/hooks/toggle/useToggle';
import { _ } from '/@/locale';
import MuiDatePicker from '/@/mui/datepicker';
import { getCropConfig } from '/@/store/private-data/recommendations/selectors';
import { updateActiveRecommendationCropConfig } from '/@/store/recommendations/actions';
import { getActiveRecommendation } from '/@/store/recommendations/selectors';
import { getSessionCountry } from '/@/store/session/selectors';
import type { Field } from '/@/types/field';
import { createPlantingDateValidator } from '/@/utils/date';
import { useRecommendationEditData } from '/@/views/recommendations/recommendation-aio/hooks';
import ChipSelect from './chip-select';
import LastCampaignCard from './last-campaign-card';

/** Shape of the humidity overlay the map modal expects; `useHumidityMaps` is untyped. */
type HumidityMapVersion = { east: number; north: number; south: number; west: number };
type HumidityMap = { type: string; versions: HumidityMapVersion[] };

const MODEL_MARKER_COLOR = '#006A28';
const LAST_CAMPAIGN_MARKER_COLOR = '#006F9B';

const DEFAULT_ENVIRONMENT = {
    percentiles: [],
    min: 3000,
    max: 15000,
    default: 9000,
};

/**
 * "Configurar ambiente" step of the seed recommendation wizard.
 *
 * Reads and writes the same `activeRecommendation` crop config as the legacy
 * recommendation-edit page, and reuses its derivation cascade
 * (`useRecommendationEditData`) so the two stay in sync. Mounted only once a
 * field is selected — the cascade fires requests keyed on the field's location.
 */
function EnvironmentStep({ field, crop = 'corn' }: { field: Field; crop?: 'corn' | 'soy' }) {
    const dispatch = useDispatch();
    // Same breakpoint the wizard shell uses to swap its sidebar, so the step switches layout
    // exactly when the surrounding chrome does.
    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
    const recommendation = useSelector(getActiveRecommendation);
    const cropConfig = getCropConfig(recommendation, crop);
    const sessionCountry = useSelector(getSessionCountry);

    const { toggle: toggleHumidityMapModal, open: humidityMapModalOpened } = useToggle(false);
    const [mapVersion, setMapVersion] = useState<HumidityMapVersion | undefined>(undefined);
    const { data: maps } = useHumidityMaps({ country: sessionCountry?.code });
    const humidityMaps = ((maps || []) as HumidityMap[]).find((map) => map.type === 'humidity');

    useEffect(() => {
        if (humidityMaps?.versions) setMapVersion(humidityMaps.versions[0]);
    }, [humidityMaps]);

    const handleChange = (key: string) => {
        return async (value: unknown) => {
            if (['plantingDate', 'environment'].includes(key)) {
                await dispatch(updateActiveRecommendationCropConfig({ key: 'products', value: [] }));
            }
            await dispatch(updateActiveRecommendationCropConfig({ key, value }));
        };
    };

    const { soilMoisturesQuery, environmentsQuery, validPlantingDates, climatePatternChoices, isLoading } =
        useRecommendationEditData({ field, crop, handleChange });

    const { lastCampaign } = useLastCampaign(field?.id);

    // Planting windows come from the field's zones; everything outside them is greyed out
    // in the calendar rather than being selectable and then rejected.
    const shouldDisableDate = useMemo(() => createPlantingDateValidator(validPlantingDates), [validPlantingDates]);

    // The slider drags against local state so a drag doesn't write to the store —
    // and therefore doesn't clear the selected products — on every tick.
    const [draftEnvironment, setDraftEnvironment] = useState<number | undefined>(undefined);
    // Drops the draft whenever the store's environment changes — including when the cascade
    // re-defaults it — so the slider never shows a stale drag value.
    // biome-ignore lint/correctness/useExhaustiveDependencies: reset keyed on the store value
    useEffect(() => {
        setDraftEnvironment(undefined);
    }, [cropConfig?.environment]);

    if (!cropConfig) {
        return (
            <Stack alignItems='center' py={6}>
                <Spinner />
            </Stack>
        );
    }

    const environment = environmentsQuery.data || DEFAULT_ENVIRONMENT;
    const soilMoistureChoices = soilMoisturesQuery.data || [];

    const markers: EnvironmentSliderMarker[] = [
        {
            value: environment.default,
            label: _('Sugerido del modelo'),
            color: MODEL_MARKER_COLOR,
            lineHeight: lastCampaign ? 100 : 40,
        },
        ...(lastCampaign
            ? [
                  {
                      value: lastCampaign.averageYield,
                      label: _('Rendimiento última campaña'),
                      color: LAST_CAMPAIGN_MARKER_COLOR,
                      lineHeight: 40,
                  },
              ]
            : []),
    ];

    return (
        <Stack spacing={4} pb={4}>
            <Stack direction={isMobile ? 'column' : 'row'} spacing={3} alignItems='flex-start'>
                <Stack
                    spacing={2}
                    flexGrow={1}
                    sx={{
                        width: isMobile ? '100%' : 'auto',
                    }}
                >
                    <Typography variant='subtitle1' fontWeight={700} color='primary'>
                        {_('Fecha de siembra')}
                    </Typography>
                    <MuiDatePicker
                        value={cropConfig.plantingDate ? dayjs(cropConfig.plantingDate) : null}
                        onChange={(date) => handleChange('plantingDate')(date ? date.format('YYYY-MM-DD') : '')}
                        shouldDisableDate={shouldDisableDate}
                        disabled={isLoading}
                    />
                </Stack>

                {soilMoistureChoices.length > 0 && (
                    <ChipSelect
                        title={_('Humedad de suelo')}
                        options={soilMoistureChoices}
                        value={cropConfig.soilMoisture}
                        onChange={handleChange('soilMoisture')}
                        disabled={isLoading}
                        stretch={isMobile}
                        dataTestId='environment-step-soil-moisture-chips'
                        action={
                            // The humidity map needs a full-size map canvas, so the design drops
                            // the shortcut on phones.
                            !isMobile &&
                            field.country?.code === 'AR' && (
                                <Button
                                    variant='text'
                                    color='primary'
                                    startIcon={<MapIcon />}
                                    onClick={toggleHumidityMapModal}
                                    sx={{ textDecoration: 'underline', p: 0 }}
                                >
                                    {_('Ver mapa')}
                                </Button>
                            )
                        }
                    />
                )}

                {crop === 'corn' && (
                    <ChipSelect
                        title={_('Año climático')}
                        options={climatePatternChoices || getClimatePatternChoices()}
                        value={cropConfig.climatePattern}
                        onChange={handleChange('climatePattern')}
                        disabled={isLoading}
                        stretch={isMobile}
                        dataTestId='environment-step-climate-pattern-chips'
                    />
                )}
            </Stack>

            <Stack direction={isMobile ? 'column' : 'row'} spacing={4} alignItems='flex-start'>
                <Stack spacing={4} flexGrow={1} width='100%'>
                    <Stack spacing={0.5}>
                        <Typography variant='subtitle1' fontWeight={700} color='primary'>
                            {_('Ambiente del lote')}
                        </Typography>
                        <Typography variant='body2' color='primary'>
                            {_(
                                'La barra muestra el rendimiento objetivo del lote según nuestro modelo de datos, y define qué híbridos y qué densidad se recomiendan.'
                            )}
                        </Typography>
                    </Stack>
                    <EnvironmentSliderV2
                        percentiles={environment.percentiles}
                        min={environment.min}
                        max={environment.max}
                        value={draftEnvironment ?? cropConfig.environment ?? environment.default}
                        onChange={(_event, value) => setDraftEnvironment(value)}
                        onChangeCommitted={(_event, value) => handleChange('environment')(value)}
                        valueLabelDisplay='on'
                        disabled={isLoading}
                        markers={markers}
                        markersVariant={isMobile ? 'list' : 'annotations'}
                    />
                </Stack>
                {lastCampaign && (
                    <LastCampaignCard fieldName={field.name} lastCampaign={lastCampaign} compact={isMobile} />
                )}
            </Stack>

            {humidityMapModalOpened && (
                <HumidityMapModal
                    open={humidityMapModalOpened}
                    toggleHumidityMapModal={toggleHumidityMapModal}
                    field={field}
                    mapVersion={{ version: mapVersion, overlay: humidityMaps }}
                    handleOverlayChange={(overlay) => {
                        if (typeof overlay?.version === 'number')
                            setMapVersion(humidityMaps?.versions[overlay.version]);
                    }}
                />
            )}
        </Stack>
    );
}

export default EnvironmentStep;
